import { useCallback, useEffect, useMemo, useState } from 'react';
import { getUsableEmail } from '@car-pool/shared/contactIdentity';
import { formatContactName, parseChildrenIds } from '@car-pool/shared/schoolRecords';
import { apiFetch } from '../config/api';
import ResetCodeButton from '../components/ResetCodeButton';
import SendOnboardingEmailButton from '../components/SendOnboardingEmailButton';
import SendOnboardingWhatsAppButton from '../components/SendOnboardingWhatsAppButton';
import SchoolAvatar from '../components/SchoolAvatar';
import SchoolDirectoryRow from '../components/SchoolDirectoryRow';
import SchoolLayout from '../components/SchoolLayout';
import ParentProfileCard from '../components/ParentProfileCard';
import { isSuperAdminUser } from '../lib/adminAccess';

const RELATIONSHIP_OPTIONS = ['Parent', 'Guardian', 'Sibling', 'Spouse', 'Relative', 'Friend', 'Neighbor', 'Other'];
const ONBOARDING_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'onboarded', label: 'Onboarded' },
  { key: 'not-onboarded', label: 'Not onboarded' },
];

function compareByName(a, b) {
  const left = `${a.l_name || ''} ${a.f_name || ''}`.trim().toLowerCase();
  const right = `${b.l_name || ''} ${b.f_name || ''}`.trim().toLowerCase();
  return left.localeCompare(right);
}

function getAuthorizationLabel(status, endDate) {
  if (!status && !endDate) return 'No expiration set';

  const formattedDate = endDate
    ? new Date(`${endDate}T00:00:00`).toLocaleDateString([], { month: 'short', day: 'numeric' })
    : null;

  switch (status) {
    case 'accepted':
      return formattedDate ? `Approved through ${formattedDate}` : 'Approved';
    case 'pending':
      return formattedDate ? `Pending through ${formattedDate}` : 'Pending approval';
    case 'revoked':
      return formattedDate ? `Revoked on ${formattedDate}` : 'Revoked';
    case 'declined':
      return formattedDate ? `Declined on ${formattedDate}` : 'Declined';
    case 'expired':
      return formattedDate ? `Expired ${formattedDate}` : 'Expired';
    default:
      return formattedDate ? `Valid through ${formattedDate}` : 'Authorization recorded';
  }
}

function formatDateShort(value) {
  if (!value) return 'Not assigned';

  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return 'Not assigned';

  return date.toLocaleDateString([], { month: '2-digit', day: '2-digit', year: '2-digit' });
}

function isoDateForOffset(days = 0) {
  const date = new Date();
  date.setHours(12, 0, 0, 0);
  date.setDate(date.getDate() + days);
  return date.toISOString().slice(0, 10);
}

function buildDriverForm(defaultStudentId = '') {
  return {
    student_id: defaultStudentId ? String(defaultStudentId) : '',
    f_name: '',
    l_name: '',
    relationship: 'Friend',
    phone: '',
    email: '',
    street_address: '',
    city: '',
    state: '',
    zip_code: '',
    driver_license_number: '',
    license_plate: '',
    vehicle_make_model: '',
    car_color: '',
    start_date: isoDateForOffset(0),
    end_date: isoDateForOffset(1),
    notes: '',
  };
}

function buildParentAccountMarker(parentId) {
  return `PARENT_ACCOUNT:${parentId}`;
}

export default function ParentList({ token, navigate, schoolId, user, selectParentId = null, bare = false }) {
  const [contacts, setContacts] = useState([]);
  const [students, setStudents] = useState([]);
  const [authorizations, setAuthorizations] = useState([]);
  const [dismissalStatuses, setDismissalStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [onboardingFilter, setOnboardingFilter] = useState('all');
  const [selectedParentId, setSelectedParentId] = useState(selectParentId || null);
  const [manualCheckInId, setManualCheckInId] = useState(null);
  const [manualCheckInMessage, setManualCheckInMessage] = useState('');
  const [deleteConfirmation, setDeleteConfirmation] = useState(null);
  const [deleteSavingScope, setDeleteSavingScope] = useState('');
  const [deleteError, setDeleteError] = useState('');

  useEffect(() => {
    if (selectParentId) setSelectedParentId(selectParentId);
  }, [selectParentId]);
  const [authorizationExpanded, setAuthorizationExpanded] = useState(false);
  const [authorizationSearch, setAuthorizationSearch] = useState('');
  const [authorizationError, setAuthorizationError] = useState('');
  const [authorizationSaving, setAuthorizationSaving] = useState(false);
  const [authorizationDetail, setAuthorizationDetail] = useState(null);
  const [addDriverOpen, setAddDriverOpen] = useState(false);
  const [addDriverError, setAddDriverError] = useState('');
  const [addDriverSaving, setAddDriverSaving] = useState(false);
  const [driverForm, setDriverForm] = useState(buildDriverForm());

  const load = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const [contactsRes, studentsRes, authorizationsRes, dismissalStatusesRes] = await Promise.all([
        apiFetch(`/contacts?school_id=${schoolId}`, {}, token),
        apiFetch(`/students?school_id=${schoolId}`, {}, token),
        apiFetch(`/temporary-authorized-drivers/school/${schoolId}`, {}, token).catch(() => ({ data: [] })),
        apiFetch(`/dismissal-status?school_id=${schoolId}`, {}, token).catch(() => ({ data: [] })),
      ]);

      setContacts(contactsRes.data || []);
      setStudents(studentsRes.data || []);
      setAuthorizations(authorizationsRes.data || []);
      setDismissalStatuses(dismissalStatusesRes.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [schoolId, token]);

  useEffect(() => {
    load();
  }, [load]);

  const parents = useMemo(
    () => {
      const list = [...contacts].filter((contact) => contact.type === 'parent');
      // Someone opened by id — from master search, or a link on another page —
      // may not be in this list at all: a guardian is not type `parent`. Show
      // the person who was asked for rather than silently falling back to the
      // first row, which is what the selection effect below would otherwise do.
      const requested = selectParentId
        && !list.some((contact) => Number(contact.id) === Number(selectParentId))
        && contacts.find((contact) => (
          Number(contact.id) === Number(selectParentId)
          && ['parent', 'guardian'].includes(String(contact.type || '').toLowerCase())
        ));
      if (requested) list.push(requested);
      return list.sort(compareByName);
    },
    [contacts, selectParentId]
  );

  const onboardingCounts = useMemo(() => {
    const onboarded = parents.filter((parent) => parent.has_password).length;
    return {
      all: parents.length,
      onboarded,
      'not-onboarded': parents.length - onboarded,
    };
  }, [parents]);

  const filteredParents = useMemo(() => {
    const query = search.trim().toLowerCase();

    return parents.filter((parent) => {
      if (onboardingFilter === 'onboarded' && !parent.has_password) return false;
      if (onboardingFilter === 'not-onboarded' && parent.has_password) return false;
      if (!query) return true;

      const fields = [
        parent.f_name,
        parent.l_name,
        parent.email,
        parent.phone,
        parent.city,
        parent.state,
      ];

      return fields.some((field) => String(field || '').toLowerCase().includes(query));
    });
  }, [onboardingFilter, parents, search]);

  useEffect(() => {
    if (loading) return;

    if (!filteredParents.length) {
      setSelectedParentId(null);
      return;
    }

    const selectedStillExists = filteredParents.some((parent) => parent.id === selectedParentId);
    if (!selectedStillExists) {
      setSelectedParentId(filteredParents[0].id);
    }
  }, [loading, filteredParents, selectedParentId]);

  useEffect(() => {
    setAuthorizationExpanded(false);
    setAuthorizationSearch('');
    setAuthorizationError('');
    setAuthorizationDetail(null);
    setAddDriverOpen(false);
    setAddDriverError('');
    setManualCheckInMessage('');
  }, [selectedParentId]);

  const contactsById = useMemo(
    () => Object.fromEntries(contacts.map((contact) => [contact.id, contact])),
    [contacts]
  );

  const studentsById = useMemo(
    () => Object.fromEntries(students.map((student) => [student.id, student])),
    [students]
  );

  const dismissalByStudentId = useMemo(
    () => Object.fromEntries(dismissalStatuses.map((status) => [status.student_id, status])),
    [dismissalStatuses]
  );

  const selectedParent = useMemo(
    () => parents.find((parent) => parent.id === selectedParentId) || null,
    [parents, selectedParentId]
  );

  const linkedStudents = useMemo(() => {
    if (!selectedParent) return [];

    const childrenIds = parseChildrenIds(selectedParent.children_ids);
    return childrenIds
      .map((childId) => studentsById[childId])
      .filter(Boolean);
  }, [selectedParent, studentsById]);

  useEffect(() => {
    setDriverForm(buildDriverForm(linkedStudents[0]?.id || ''));
  }, [selectedParentId, linkedStudents]);

  const pickupAuthorizations = useMemo(() => {
    if (!selectedParent) return [];

    const linkedStudentIds = new Set(linkedStudents.map((student) => student.id));
    const statusRank = { accepted: 0, pending: 1, expired: 2, revoked: 3, declined: 4 };

    return [...authorizations]
      .filter((record) =>
        linkedStudentIds.has(record.student_id) ||
        record.authorizing_contact_id === selectedParent.id ||
        record.authorized_contact_id === selectedParent.id
      )
      .sort((left, right) => {
        const statusDiff = (statusRank[left.status] ?? 99) - (statusRank[right.status] ?? 99);
        if (statusDiff !== 0) return statusDiff;
        return String(right.updated_at || '').localeCompare(String(left.updated_at || ''));
      })
      .map((record) => ({
        ...record,
        authorizer: contactsById[record.authorizing_contact_id],
        authorized: contactsById[record.authorized_contact_id],
        student: studentsById[record.student_id],
      }));
  }, [authorizations, contactsById, linkedStudents, selectedParent, studentsById]);

  const approvedAuthorizations = useMemo(
    () => pickupAuthorizations.filter((record) => record.status === 'accepted'),
    [pickupAuthorizations]
  );

  const parentAccountDrivers = useMemo(() => {
    if (!selectedParent) return [];

    const parentChildrenIds = new Set(parseChildrenIds(selectedParent.children_ids));
    const parentMarker = buildParentAccountMarker(selectedParent.id);

    return contacts
      .filter((contact) => contact.type === 'driver')
      .filter((contact) => {
        if (contact.emergency_backup_phone === parentMarker) {
          return true;
        }

        const driverChildrenIds = parseChildrenIds(contact.children_ids);
        return driverChildrenIds.some((childId) => parentChildrenIds.has(childId));
      })
      .sort(compareByName);
  }, [contacts, selectedParent]);

  const reusableAuthorizedDrivers = useMemo(() => {
    if (!selectedParent) return [];

    const approvedIds = new Set(
      approvedAuthorizations
        .map((record) => Number(record.authorized_contact_id))
        .filter((id) => Number.isFinite(id) && id > 0)
    );

    return [...new Map(
      [
        ...pickupAuthorizations
          .map((record) => record.authorized)
          .filter(Boolean),
        ...parentAccountDrivers,
      ]
        .map((contact) => [contact.id, contact])
    ).values()]
      .filter((contact) => !approvedIds.has(Number(contact.id)))
      .sort(compareByName);
  }, [approvedAuthorizations, parentAccountDrivers, pickupAuthorizations, selectedParent]);

  const pickupAuthorizationDrivers = useMemo(() => {
    return parentAccountDrivers.map((contact) => {
      const driverAuthorizations = approvedAuthorizations.filter(
        (record) => Number(record.authorized_contact_id) === Number(contact.id)
      );

      const linkedStudentNames = driverAuthorizations
        .map((record) => record.student ? `${record.student.f_name} ${record.student.l_name}` : null)
        .filter(Boolean);

      let meta = 'Added to parent account';
      if (linkedStudentNames.length === 1) {
        meta = linkedStudentNames[0];
      } else if (linkedStudentNames.length > 1) {
        meta = `Authorized for ${linkedStudentNames.length} students`;
      }

      return {
        contact,
        authorizations: driverAuthorizations,
        meta,
      };
    });
  }, [approvedAuthorizations, parentAccountDrivers]);

  const filteredReusableDrivers = useMemo(() => {
    const query = authorizationSearch.trim().toLowerCase();
    if (!query) return [];

    return reusableAuthorizedDrivers.filter((contact) => {
      const fields = [
        formatContactName(contact),
        contact.phone,
        contact.email,
      ];

      return fields.some((field) => String(field || '').toLowerCase().includes(query));
    });
  }, [authorizationSearch, reusableAuthorizedDrivers]);

  const openDeleteConfirmation = (parent) => {
    setDeleteError('');
    setDeleteConfirmation(parent);
  };

  const closeDeleteConfirmation = () => {
    if (deleteSavingScope) return;
    setDeleteError('');
    setDeleteConfirmation(null);
  };

  const handleDelete = async (scope) => {
    if (!deleteConfirmation?.id || deleteSavingScope) return;

    const confirmationSchoolIds = parseChildrenIds(deleteConfirmation.school_ids);
    setDeleteSavingScope(scope);
    setDeleteError('');

    try {
      await apiFetch(`/contacts/${deleteConfirmation.id}`, {
        method: 'DELETE',
        body: JSON.stringify({
          scope,
          school_id: Number(schoolId),
          confirmation_school_ids: confirmationSchoolIds,
          ...(scope === 'all' ? {
            confirm_all_schools: true,
          } : {}),
        }),
      }, token);
      setDeleteConfirmation(null);
      await load();
    } catch (err) {
      if (err.data?.confirmation_required && Array.isArray(err.data.school_ids)) {
        setDeleteConfirmation((current) => current
          ? { ...current, school_ids: err.data.school_ids }
          : current);
      }
      setDeleteError(err.message);
    } finally {
      setDeleteSavingScope('');
    }
  };

  const handleManualCheckIn = async (parent) => {
    if (!parent?.id || manualCheckInId) return;

    setManualCheckInId(parent.id);
    setManualCheckInMessage('');

    try {
      const res = await apiFetch('/geofenced-data/manual-entry', {
        method: 'POST',
        body: JSON.stringify({
          school_id: schoolId,
          parent_driver_id: parent.id,
        }),
      }, token);
      const calledCount = res.data?.called_student_ids?.length || 0;
      setManualCheckInMessage(`${formatContactName(parent)} marked in zone${calledCount ? `; ${calledCount} students called` : ''}.`);
      await load();
    } catch (err) {
      setManualCheckInMessage(err.message || 'Unable to mark parent in zone.');
    } finally {
      setManualCheckInId(null);
    }
  };

  const openAuthorizationBuilder = () => {
    setAuthorizationExpanded(true);
    setAuthorizationSearch('');
    setAuthorizationError('');
  };

  const closeAuthorizationBuilder = () => {
    if (authorizationSaving) return;
    setAuthorizationExpanded(false);
    setAuthorizationSearch('');
    setAuthorizationError('');
  };

  const openAddDriverModal = () => {
    setAddDriverError('');
    setDriverForm(buildDriverForm(linkedStudents[0]?.id || ''));
    setAddDriverOpen(true);
  };

  const closeAddDriverModal = () => {
    if (addDriverSaving) return;
    setAddDriverOpen(false);
    setAddDriverError('');
  };

  const closeAuthorizationDetail = () => {
    if (authorizationSaving) return;
    setAuthorizationDetail(null);
    setAuthorizationError('');
  };

  const authorizeExistingDriver = async () => {
    if (!authorizationDetail?.contact?.id || !selectedParent?.id || !authorizationDetail?.studentId) {
      setAuthorizationError('Select a student before authorizing this driver.');
      return;
    }

    setAuthorizationSaving(true);
    setAuthorizationError('');

    try {
      await apiFetch('/temporary-authorized-drivers', {
        method: 'POST',
        body: JSON.stringify({
          school_id: schoolId,
          student_id: Number(authorizationDetail.studentId),
          authorizing_contact_id: selectedParent.id,
          authorized_contact_id: authorizationDetail.contact.id,
          start_date: authorizationDetail.startDate,
          end_date: authorizationDetail.endDate,
          status: 'accepted',
          notes: null,
        }),
      }, token);

      await load();
      setAuthorizationDetail(null);
      setAuthorizationExpanded(false);
      setAuthorizationSearch('');
    } catch (err) {
      setAuthorizationError(err.message);
    } finally {
      setAuthorizationSaving(false);
    }
  };

  const handleCreateDriver = async (event) => {
    event.preventDefault();
    setAddDriverSaving(true);
    setAddDriverError('');

    try {
      if (driverForm.state.trim().length !== 2) {
        throw new Error('State must be a 2-letter code.');
      }

      if (new Date(driverForm.end_date) <= new Date(driverForm.start_date)) {
        throw new Error('End date must be after the start date.');
      }

      const contactRes = await apiFetch('/contacts', {
        method: 'POST',
        body: JSON.stringify({
          type: 'driver',
          school_ids: [schoolId],
          children_ids: parseChildrenIds(selectedParent.children_ids),
          f_name: driverForm.f_name.trim(),
          l_name: driverForm.l_name.trim(),
          phone: driverForm.phone.trim(),
          email: driverForm.email.trim(),
          emergency_backup_phone: buildParentAccountMarker(selectedParent.id),
          emergency_relationship: driverForm.relationship,
          street_address: driverForm.street_address.trim(),
          city: driverForm.city.trim(),
          state: driverForm.state.trim().toUpperCase(),
          zip_code: driverForm.zip_code.trim(),
          driver_license_number: driverForm.driver_license_number.trim(),
          photo_relative_path: 'placeholder.jpg',
          vehicle_make_model: driverForm.vehicle_make_model.trim() || null,
          car_color: driverForm.car_color.trim() || null,
          license_plate: driverForm.license_plate.trim(),
          notifications_enabled: false,
        }),
      }, token);

      if (driverForm.student_id) {
        await apiFetch('/temporary-authorized-drivers', {
          method: 'POST',
          body: JSON.stringify({
            school_id: schoolId,
            student_id: Number(driverForm.student_id),
            authorizing_contact_id: selectedParent.id,
            authorized_contact_id: contactRes.data.id,
            start_date: driverForm.start_date,
            end_date: driverForm.end_date,
            status: 'accepted',
            notes: driverForm.notes.trim() || null,
          }),
        }, token);
      }

      await load();
      setAddDriverOpen(false);
      setAuthorizationExpanded(true);
      setAuthorizationSearch(contactRes.data?.f_name || '');
    } catch (err) {
      setAddDriverError(err.message);
    } finally {
      setAddDriverSaving(false);
    }
  };

  const content = (
    <div className="sap-page">
        {error && <div className="sa-error sap-inline-error">{error}</div>}

        {loading ? (
          <div className="sa-empty">Loading...</div>
        ) : (
          <div className="sap-parents-shell">
            <section className="sap-parents-panel sap-directory-panel">
              <div className="sap-panel-heading">
                <div>
                  <h2>Parents</h2>
                  <p>View, manage, and track all parents and drivers</p>
                </div>
                <button className="sa-btn sa-btn-primary" onClick={() => navigate('parent-create', schoolId)}>
                  + Add Parent
                </button>
              </div>

              <div className="sap-directory-list">
                {filteredParents.length === 0 ? (
                  <div className="sa-empty sap-compact-empty">
                    {parents.length === 0 ? 'No parents found for this school.' : 'No parents match your search.'}
                  </div>
                ) : (
                  filteredParents.map((parent) => {
                    const fullName = formatContactName(parent);
                    const cityState = [parent.city, parent.state].filter(Boolean).join(', ');
                    const onboardingStatus = parent.has_password ? 'Onboarded' : 'Not onboarded';

                    return (
                      <SchoolDirectoryRow
                        key={parent.id}
                        record={parent}
                        name={fullName}
                        meta={[onboardingStatus, cityState || parent.phone || getUsableEmail(parent) || 'No contact details'].join(' - ')}
                        active={selectedParent?.id === parent.id}
                        onClick={() => setSelectedParentId(parent.id)}
                      />
                    );
                  })
                )}
              </div>

              <div className="sap-directory-footer">
                <div className="sap-onboarding-filter" role="group" aria-label="Parent onboarding filter">
                  {ONBOARDING_FILTERS.map((filter) => (
                    <button
                      key={filter.key}
                      type="button"
                      className={`sap-onboarding-filter-btn ${onboardingFilter === filter.key ? 'active' : ''}`}
                      onClick={() => setOnboardingFilter(filter.key)}
                    >
                      <span>{filter.label}</span>
                      <strong>{onboardingCounts[filter.key]}</strong>
                    </button>
                  ))}
                </div>
                <label className="sap-directory-search">
                  <span>⌕</span>
                  <input
                    type="text"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="Search"
                  />
                </label>
              </div>
            </section>

            <section className="sap-parents-panel sap-detail-panel">
              <div className="sap-panel-heading">
                <div>
                  <h2>Detailed view</h2>
                  <p>Manage and view parent in detail</p>
                </div>
              </div>

              {selectedParent ? (
                <>
                  <ParentProfileCard
                    parent={selectedParent}
                    linkedStudents={linkedStudents}
                    dismissalByStudentId={dismissalByStudentId}
                  />

                  <div className="sap-parent-actions">
                    <button
                      className="sa-btn sa-btn-primary"
                      onClick={() => handleManualCheckIn(selectedParent)}
                      disabled={Number(manualCheckInId) === Number(selectedParent.id)}
                    >
                      {Number(manualCheckInId) === Number(selectedParent.id) ? 'Marking…' : 'Mark In Zone'}
                    </button>
                    <button
                      className="sa-btn sa-btn-secondary"
                      onClick={() => navigate('parent-edit', { parent: selectedParent, schoolId })}
                    >
                      Edit Parent
                    </button>
                    <button
                      className="sa-btn sa-btn-secondary"
                      onClick={() => navigate('parent-detail', { parentId: selectedParent.id, schoolId })}
                    >
                      Manage Students
                    </button>
                    <ResetCodeButton
                      token={token}
                      userType="contact"
                      userId={selectedParent.id}
                      userName={formatContactName(selectedParent)}
                      contact={selectedParent}
                    />
                    <SendOnboardingEmailButton
                      token={token}
                      parentId={selectedParent.id}
                      schoolId={schoolId}
                      hasPassword={selectedParent.has_password}
                      contact={selectedParent}
                    />
                    <SendOnboardingWhatsAppButton
                      token={token}
                      parentId={selectedParent.id}
                      schoolId={schoolId}
                      hasPassword={selectedParent.has_password}
                    />
                    <button
                      className="sa-btn sa-btn-danger"
                      onClick={() => openDeleteConfirmation(selectedParent)}
                    >
                      Delete
                    </button>
                  </div>
                  {manualCheckInMessage ? (
                    <div className="sap-manual-checkin-message">{manualCheckInMessage}</div>
                  ) : null}
                </>
              ) : (
                <div className="sa-empty sap-compact-empty">Select a parent to view details.</div>
              )}
            </section>

            <section className="sap-parents-panel sap-authorization-panel">
              <div className="sap-panel-heading">
                <div>
                  <h2>Pickup Authorization</h2>
                  <p>View and track all parents and authorized drivers</p>
                </div>
              </div>

              <div className="sap-section-card sap-authorization-card">
                <div className="sap-authorization-topline">
                  <div className="sap-section-caption">Approved authorized drivers</div>
                  <button
                    type="button"
                    className="sap-authorization-toggle"
                    onClick={authorizationExpanded ? closeAuthorizationBuilder : openAuthorizationBuilder}
                  >
                    {authorizationExpanded ? '−' : '+'}
                  </button>
                </div>

                {authorizationExpanded ? (
                  <div className="sap-authorization-builder">
                    <button type="button" className="sap-authorization-add-link" onClick={openAddDriverModal}>
                      Add Temporary Driver
                    </button>

                    <input
                      className="sap-authorization-search"
                      type="text"
                      value={authorizationSearch}
                      onChange={(event) => setAuthorizationSearch(event.target.value)}
                      placeholder="Search by name, phone number, or email."
                    />

                    {authorizationError && <div className="sa-error sap-inline-error">{authorizationError}</div>}

                    {filteredReusableDrivers.length > 0 ? (
                      <div className="sap-authorization-search-list">
                        {filteredReusableDrivers.map((contact) => (
                          <button
                            type="button"
                            key={contact.id}
                            className="sap-authorization-search-row"
                            onClick={() => setAuthorizationDetail({
                              mode: 'authorize',
                              contact,
                              studentId: linkedStudents[0]?.id ? String(linkedStudents[0].id) : '',
                              startDate: isoDateForOffset(0),
                              endDate: isoDateForOffset(1),
                            })}
                          >
                            <div className="sap-authorization-search-person">
                              <SchoolAvatar name={formatContactName(contact)} seed={contact.id} />
                              <div className="sap-authorization-name">{formatContactName(contact)}</div>
                            </div>
                            <span className="sap-authorization-arrow">→</span>
                          </button>
                        ))}
                      </div>
                    ) : authorizationSearch.trim() ? (
                      <div className="sap-section-empty">No previously added drivers match your search.</div>
                    ) : null}
                  </div>
                ) : pickupAuthorizationDrivers.length === 0 ? (
                  <div className="sap-section-empty">
                    No drivers have been added to this parent account yet.
                  </div>
                ) : (
                  pickupAuthorizationDrivers.map(({ contact, authorizations, meta }) => {
                    const contactName = formatContactName(contact);
                    const firstAuthorization = authorizations[0] || null;

                    return (
                      <button
                        type="button"
                        key={contact.id}
                        className="sap-authorization-driver-row"
                        onClick={() => setAuthorizationDetail({
                          mode: 'view',
                          record: firstAuthorization,
                          contact,
                          studentId: firstAuthorization?.student_id ? String(firstAuthorization.student_id) : '',
                          startDate: firstAuthorization?.start_date || '',
                          endDate: firstAuthorization?.end_date || '',
                          meta,
                        })}
                      >
                        <div className="sap-authorization-person">
                          <SchoolAvatar name={contactName} seed={contact.id} />
                          <div>
                            <div className="sap-authorization-name">{contactName}</div>
                            <div className="sap-authorization-meta">{meta}</div>
                          </div>
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            </section>
          </div>
        )}

        {authorizationDetail ? (
          <div className="sas-modal-overlay" onClick={closeAuthorizationDetail}>
            <div className="sas-modal-popout sap-authorization-modal" onClick={(event) => event.stopPropagation()}>
              <button type="button" className="sas-modal-back" onClick={closeAuthorizationDetail}>←</button>
              <div className="sap-authorization-modal-hero">
                <SchoolAvatar name={formatContactName(authorizationDetail.contact)} seed={authorizationDetail.contact?.id || 0} />
                <div>
                  <h3>{formatContactName(authorizationDetail.contact)}</h3>
                  {authorizationDetail.contact?.phone ? <div>{authorizationDetail.contact.phone}</div> : null}
                  {getUsableEmail(authorizationDetail.contact) ? <div>{getUsableEmail(authorizationDetail.contact)}</div> : null}
                </div>
              </div>

              {authorizationDetail.mode === 'authorize' ? (
                <>
                  <label className="sap-authorization-field">
                    <span>Student</span>
                    <select
                      value={authorizationDetail.studentId}
                      onChange={(event) => setAuthorizationDetail((prev) => ({ ...prev, studentId: event.target.value }))}
                    >
                      <option value="">Select student</option>
                      {linkedStudents.map((student) => (
                        <option key={student.id} value={student.id}>
                          {student.f_name} {student.l_name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="sap-authorization-field sap-authorization-field-split">
                    <span>Start Date:</span>
                    <input
                      type="date"
                      value={authorizationDetail.startDate}
                      onChange={(event) => setAuthorizationDetail((prev) => ({ ...prev, startDate: event.target.value }))}
                    />
                  </label>
                  <label className="sap-authorization-field sap-authorization-field-split">
                    <span>End Date:</span>
                    <input
                      type="date"
                      value={authorizationDetail.endDate}
                      onChange={(event) => setAuthorizationDetail((prev) => ({ ...prev, endDate: event.target.value }))}
                    />
                  </label>

                  <button type="button" className="sa-btn sa-btn-primary sap-authorization-submit" onClick={authorizeExistingDriver} disabled={authorizationSaving}>
                    {authorizationSaving ? 'Authorizing…' : 'Authorize'}
                  </button>
                </>
              ) : (
                <>
                  <div className="sap-authorization-field sap-authorization-field-split">
                    <span>Start Date:</span>
                    <strong>{formatDateShort(authorizationDetail.startDate)}</strong>
                  </div>
                  <div className="sap-authorization-field sap-authorization-field-split">
                    <span>End Date:</span>
                    <strong>{formatDateShort(authorizationDetail.endDate)}</strong>
                  </div>
                  <div className="sap-authorization-field sap-authorization-field-split">
                    <span>Status:</span>
                    <strong>
                      {authorizationDetail.record
                        ? getAuthorizationLabel(authorizationDetail.record?.status, authorizationDetail.record?.end_date)
                        : 'Added to parent account'}
                    </strong>
                  </div>
                </>
              )}
            </div>
          </div>
        ) : null}

        {deleteConfirmation ? (() => {
          const parentName = formatContactName(deleteConfirmation);
          const parentSchoolIds = parseChildrenIds(deleteConfirmation.school_ids);
          const isMultiSchool = parentSchoolIds.length > 1;
          const canDeleteEverywhere = isMultiSchool && isSuperAdminUser(user);

          return (
            <div className="sas-modal-overlay sap-delete-modal-overlay" onClick={closeDeleteConfirmation}>
              <div
                className="sas-modal-popout sap-delete-modal"
                onClick={(event) => event.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-labelledby="sap-delete-parent-title"
              >
                <button
                  type="button"
                  className="sas-modal-back"
                  onClick={closeDeleteConfirmation}
                  disabled={Boolean(deleteSavingScope)}
                  aria-label="Close"
                >
                  ×
                </button>

                <div className="sap-delete-warning-icon" aria-hidden="true">!</div>
                <h2 id="sap-delete-parent-title">Remove {parentName}?</h2>

                {isMultiSchool ? (
                  <div className="sap-delete-warning">
                    <strong>Warning: this parent account is linked to {parentSchoolIds.length} schools.</strong>
                    <p>
                      Removing it from this school keeps the parent's login and all relationships at the other schools active.
                    </p>
                    {canDeleteEverywhere ? (
                      <p>
                        Deleting from all schools permanently removes the shared login and every student relationship across all {parentSchoolIds.length} schools.
                      </p>
                    ) : null}
                  </div>
                ) : (
                  <div className="sap-delete-warning">
                    <strong>This is the parent's only school.</strong>
                    <p>Removing it will permanently delete the account and prevent the parent from signing in.</p>
                  </div>
                )}

                {deleteError ? <div className="sa-error sap-inline-error">{deleteError}</div> : null}

                <div className="sap-delete-actions">
                  <button
                    type="button"
                    className="sa-btn sa-btn-secondary"
                    onClick={closeDeleteConfirmation}
                    disabled={Boolean(deleteSavingScope)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className={isMultiSchool ? 'sa-btn sa-btn-primary' : 'sa-btn sa-btn-danger'}
                    onClick={() => handleDelete('school')}
                    disabled={Boolean(deleteSavingScope)}
                  >
                    {deleteSavingScope === 'school'
                      ? 'Removing…'
                      : (isMultiSchool ? 'Remove from this school' : 'Delete parent')}
                  </button>
                  {canDeleteEverywhere ? (
                    <button
                      type="button"
                      className="sa-btn sa-btn-danger sap-delete-all-button"
                      onClick={() => handleDelete('all')}
                      disabled={Boolean(deleteSavingScope)}
                    >
                      {deleteSavingScope === 'all'
                        ? 'Deleting everywhere…'
                        : `Delete from all ${parentSchoolIds.length} schools`}
                    </button>
                  ) : null}
                </div>
              </div>
            </div>
          );
        })() : null}

        {addDriverOpen ? (
          <div className="sas-modal-overlay" onClick={closeAddDriverModal}>
            <div className="sas-modal-popout sap-authorization-modal sap-authorization-modal-form" onClick={(event) => event.stopPropagation()}>
              <button type="button" className="sas-modal-back" onClick={closeAddDriverModal}>←</button>
              <div className="sd-student-parent-modal-copy">
                <h2>Add Temporary Driver</h2>
                <p>Create a driver for this parent account and optionally authorize them to a student now.</p>
              </div>

              {addDriverError && <div className="sa-error sap-inline-error">{addDriverError}</div>}

              <form className="sap-authorization-form" onSubmit={handleCreateDriver}>
                <label className="sap-authorization-field">
                  <span>Student</span>
                  <select value={driverForm.student_id} onChange={(event) => setDriverForm((prev) => ({ ...prev, student_id: event.target.value }))}>
                    <option value="">Add to parent account only</option>
                    {linkedStudents.map((student) => (
                      <option key={student.id} value={student.id}>
                        {student.f_name} {student.l_name}
                      </option>
                    ))}
                  </select>
                </label>

                <div className="sap-authorization-grid">
                  <label className="sap-authorization-field"><span>First Name *</span><input value={driverForm.f_name} onChange={(event) => setDriverForm((prev) => ({ ...prev, f_name: event.target.value }))} required /></label>
                  <label className="sap-authorization-field"><span>Last Name *</span><input value={driverForm.l_name} onChange={(event) => setDriverForm((prev) => ({ ...prev, l_name: event.target.value }))} required /></label>
                </div>

                <label className="sap-authorization-field">
                  <span>Relationship *</span>
                  <select value={driverForm.relationship} onChange={(event) => setDriverForm((prev) => ({ ...prev, relationship: event.target.value }))}>
                    {RELATIONSHIP_OPTIONS.map((relationship) => <option key={relationship} value={relationship}>{relationship}</option>)}
                  </select>
                </label>

                <div className="sap-authorization-grid">
                  <label className="sap-authorization-field"><span>Phone *</span><input value={driverForm.phone} onChange={(event) => setDriverForm((prev) => ({ ...prev, phone: event.target.value }))} required /></label>
                  <label className="sap-authorization-field"><span>Email *</span><input type="email" value={driverForm.email} onChange={(event) => setDriverForm((prev) => ({ ...prev, email: event.target.value }))} required /></label>
                </div>

                <label className="sap-authorization-field"><span>Street Address *</span><input value={driverForm.street_address} onChange={(event) => setDriverForm((prev) => ({ ...prev, street_address: event.target.value }))} required /></label>

                <div className="sap-authorization-grid sap-authorization-grid-third">
                  <label className="sap-authorization-field"><span>City *</span><input value={driverForm.city} onChange={(event) => setDriverForm((prev) => ({ ...prev, city: event.target.value }))} required /></label>
                  <label className="sap-authorization-field"><span>State *</span><input value={driverForm.state} maxLength={2} onChange={(event) => setDriverForm((prev) => ({ ...prev, state: event.target.value.toUpperCase() }))} required /></label>
                  <label className="sap-authorization-field"><span>ZIP *</span><input value={driverForm.zip_code} onChange={(event) => setDriverForm((prev) => ({ ...prev, zip_code: event.target.value }))} required /></label>
                </div>

                <div className="sap-authorization-grid">
                  <label className="sap-authorization-field"><span>Driver License</span><input value={driverForm.driver_license_number} onChange={(event) => setDriverForm((prev) => ({ ...prev, driver_license_number: event.target.value }))} /></label>
                  <label className="sap-authorization-field"><span>License Plate</span><input value={driverForm.license_plate} onChange={(event) => setDriverForm((prev) => ({ ...prev, license_plate: event.target.value.toUpperCase() }))} /></label>
                </div>

                <div className="sap-authorization-grid">
                  <label className="sap-authorization-field"><span>Vehicle Make/Model</span><input value={driverForm.vehicle_make_model} onChange={(event) => setDriverForm((prev) => ({ ...prev, vehicle_make_model: event.target.value }))} /></label>
                  <label className="sap-authorization-field"><span>Car Color</span><input value={driverForm.car_color} onChange={(event) => setDriverForm((prev) => ({ ...prev, car_color: event.target.value }))} /></label>
                </div>

                <div className="sap-authorization-grid">
                  <label className="sap-authorization-field"><span>Start Date *</span><input type="date" value={driverForm.start_date} onChange={(event) => setDriverForm((prev) => ({ ...prev, start_date: event.target.value }))} required /></label>
                  <label className="sap-authorization-field"><span>End Date *</span><input type="date" value={driverForm.end_date} onChange={(event) => setDriverForm((prev) => ({ ...prev, end_date: event.target.value }))} required /></label>
                </div>

                <label className="sap-authorization-field"><span>Notes</span><textarea rows="3" value={driverForm.notes} onChange={(event) => setDriverForm((prev) => ({ ...prev, notes: event.target.value }))} /></label>

                <button type="submit" className="sa-btn sa-btn-primary sap-authorization-submit" disabled={addDriverSaving}>
                  {addDriverSaving ? 'Saving…' : 'Save authorized driver'}
                </button>
              </form>
            </div>
          </div>
        ) : null}
      </div>
  );

  if (bare) return content;

  return (
    <SchoolLayout
      token={token}
      navigate={navigate}
      schoolId={schoolId}
      user={user}
      activePage="parents"
      breadcrumb="Parents"
    >
      {content}
    </SchoolLayout>
  );
}
